import { useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import LineItems from '../LineItems/LineItems';
import FutureCharges from '../FutureCharges/FutureCharges';
import styled from 'styled-components';
import Total from '../Total/Total';
import DiscountCode from '../Adjustments/DiscountCode';
import { canReadText, getWhiteness } from '../../utils/colors';
import { SubtotalAdjustments } from '../Adjustments/SubtotalAdjustments';
import { useLineItemErrors } from '../../hooks/useLineItemErrors';
import { OPEN, SUBMITTED } from '../../constants/Statuses';
import { useOptionalStates } from '../../hooks/OrderSummary/useOptionalStates';
import { sortLineItemArrays } from '../../utils/lineItem';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { isAtCheckoutLineItem, isFutureBilledCheckoutLineItems } from '../../types/utils/typeguards';
import partition from 'hs-lodash/partition';
import { getIsZeroCheckoutValid } from '../../utils/validations';
import { useSubtotals } from '../../context/SubtotalsProvider';
import useApplyDiscountCodeFromResponse from '../../hooks/DiscountCode/useApplyDiscountCodeFromResponse';
import { getLineItemBlurs } from '../../state/lineItemBlursSlice';
import { useSelector } from 'react-redux';
import { getShowAllErrors } from '../../state/errorMessagesSlice';
import { getCalculateTaxLoading, getTaxCalculationId } from '../../state/calculateTaxSlice';
import { ZERO_PERCENT_TAX_GATE } from '../../constants/gates';
import { getTaxDisplayConfig } from '../../utils/taxDisplayConfig';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Flex } from 'foundations-components/components/Flex';
import { CardSection, CardWrapper, CardHeader } from 'foundations-components/transitional/components/Card';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "OrderSummary__StyledDiv"
})(["", ""], ({
  primaryColor,
  theme
}) => {
  const backgroundColor = primaryColor;
  const borderBottomColor = getWhiteness(primaryColor) ? theme.color['border-core-subtle'] : primaryColor;
  return `
      background-color: ${backgroundColor};
      border-bottom: 1px solid ${borderBottomColor};
  `;
});
const OrderSummary = ({
  checkoutSession
}) => {
  var _checkoutSession$gate;
  const {
    status,
    currencyCode,
    userDefinedBranding,
    isDiscountCodeEnabled,
    lineItems,
    isAutomatedSalesTaxEnabled
  } = checkoutSession;
  const theme = useTheme();
  const primaryColor = (userDefinedBranding === null || userDefinedBranding === void 0 ? void 0 : userDefinedBranding.primaryColor) || theme.color['fill-accent-neutral-default'];
  const taxCalculationLoading = useSelector(getCalculateTaxLoading);
  const taxCalculationId = useSelector(getTaxCalculationId);
  const isUngatedToZeroPercentTax = (_checkoutSession$gate = checkoutSession.gates) === null || _checkoutSession$gate === void 0 ? void 0 : _checkoutSession$gate.includes(ZERO_PERCENT_TAX_GATE);
  const taxDisplayConfig = useMemo(() => {
    if (isUngatedToZeroPercentTax) {
      return null;
    }
    return getTaxDisplayConfig({
      status,
      isAutomatedSalesTaxEnabled,
      taxCalculationId,
      lineItems
    });
  }, [isUngatedToZeroPercentTax, status, isAutomatedSalesTaxEnabled, taxCalculationId, lineItems]);
  const {
    formState
  } = usePaymentFormContext();
  const {
    discountCodeResponse,
    oneTimeDiscountAmount,
    adjustmentAmounts
  } = useSubtotals();
  const isDiscountCodeApplied = formState.get('isDiscountCodeApplied');
  const sessionOpen = status === OPEN;
  const readOnly = !sessionOpen;
  const checkoutFeeAdjustment = useMemo(() => adjustmentAmounts.find(adjustment => adjustment.adjustmentType === 'CHECKOUT_FEE'), [adjustmentAmounts]);
  useApplyDiscountCodeFromResponse({
    discountCodeResponse,
    oneTimeDiscountAmount
  });
  const shouldDisplaySubtotalAndDiscountCode = (status === OPEN || isDiscountCodeApplied) && isDiscountCodeEnabled;
  const [billedAtCheckoutLineItems, futureStartDateLineItems] = partition(lineItems, isAtCheckoutLineItem);
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse && checkoutSession.subtotalsPublicResponse.paymentAmount, lineItems);
  const {
    selectedItems,
    selectionToggles
  } = useOptionalStates(billedAtCheckoutLineItems);
  const lineItemBlurs = useSelector(getLineItemBlurs);
  const showAllErrors = useSelector(getShowAllErrors);
  const {
    selectedItems: futureSelectedItems,
    selectionToggles: futureSelectionToggles
  } = futureStartDateLineItems.length === 0 ? {
    selectedItems: [],
    selectionToggles: []
  } :
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useOptionalStates(futureStartDateLineItems);
  const billedAtCheckoutLineItemErrors = useLineItemErrors({
    lineItems: billedAtCheckoutLineItems,
    blurStates: lineItemBlurs,
    showAllErrors,
    readOnly,
    selectedItems
  });
  const futureStartDateLineItemErrors = useLineItemErrors({
    lineItems: futureStartDateLineItems,
    blurStates: lineItemBlurs,
    showAllErrors,
    readOnly,
    selectedItems: futureSelectedItems
  });

  /**
   * @note 1: after we submit payment, we have to filter out the unselected
   * optional line items while BE asynchronously processes the payment
   * https://hubspot.slack.com/archives/C04D60WK0TU/p1736367641360299
   *
   * @note 2: for line items that aren't optional, selectedItems[index] will always be null.
   * similarly, for all optional line items selectedItems[index] will always be true or false
   * https://git.hubteam.com/HubSpotProtected/payments-ui/blob/master/payments-ui-app/static-1.26486/js/hooks/OrderSummary/useOptionalStates.ts#L24
   */
  const selectedBilledAtCheckoutLineItems = billedAtCheckoutLineItems.filter((item, index) => selectedItems[index] === true || selectedItems[index] === null);
  const selectedFutureStartDateLineItems = futureStartDateLineItems.filter((item, index) => futureSelectedItems[index] === true || futureSelectedItems[index] === null);
  const [sortedItems, sortedToggles, sortedErrors, sortedSelections] = sortLineItemArrays({
    lineItems: status === SUBMITTED ? selectedBilledAtCheckoutLineItems : billedAtCheckoutLineItems,
    selectionToggles,
    lineItemErrors: billedAtCheckoutLineItemErrors,
    selectedItems
  });
  const [sortedFutureItems, sortedFutureToggles, sortedFutureErrors, sortedFutureSelections] = futureStartDateLineItems.length === 0 ? [[], [], [], [], []] : sortLineItemArrays({
    lineItems: status === SUBMITTED ? selectedFutureStartDateLineItems : futureStartDateLineItems,
    selectionToggles: futureSelectionToggles,
    lineItemErrors: futureStartDateLineItemErrors,
    selectedItems: futureSelectedItems
  });
  const hasFutureCharges = sortedFutureItems.length > 0 && isFutureBilledCheckoutLineItems(sortedFutureItems);
  const maybeFutureCharges = hasFutureCharges && /*#__PURE__*/_jsx(FutureCharges, {
    futureStartDateLineItems: sortedFutureItems,
    currencyCode: currencyCode,
    lineItemErrors: sortedFutureErrors,
    readOnly: readOnly,
    selectionToggles: sortedFutureToggles,
    selectedItems: sortedFutureSelections,
    sessionOpen: sessionOpen,
    isZeroCheckout: isZeroCheckout,
    checkoutFeeAdjustment: checkoutFeeAdjustment,
    taxDisplayConfig: taxDisplayConfig
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    marginBlockEnd: "700",
    children: [/*#__PURE__*/_jsx(StyledDiv, {
      primaryColor: primaryColor,
      children: /*#__PURE__*/_jsx(CardHeader, {
        paddingBlockStart: "300",
        paddingBlockEnd: "100",
        paddingInline: "600",
        title: /*#__PURE__*/_jsx(Text, {
          color: canReadText(primaryColor, theme) ? 'text-primary-default' : 'text-core-default',
          paddingBlockEnd: "250",
          typography: "heading-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.orderSummary.header"
          })
        })
      })
    }), taxCalculationLoading && /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "tax-calculation-loading",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      blockSize: "350px",
      children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.orderSummary.calculatingTax"
      })]
    }), /*#__PURE__*/_jsxs(CardSection, {
      padding: "0",
      display: taxCalculationLoading ? 'none' : 'block',
      children: [/*#__PURE__*/_jsx(LineItems, {
        lineItems: sortedItems,
        currencyCode: currencyCode,
        lineItemErrors: sortedErrors,
        readOnly: readOnly,
        selectionToggles: sortedToggles,
        selectedItems: sortedSelections,
        sessionOpen: sessionOpen,
        taxDisplayConfig: taxDisplayConfig
      }), /*#__PURE__*/_jsx(HR, {
        distance: "medium",
        marginBlockStart: "0"
      }), shouldDisplaySubtotalAndDiscountCode && /*#__PURE__*/_jsx(DiscountCode, {
        checkoutSession: checkoutSession
      }), /*#__PURE__*/_jsx(SubtotalAdjustments, {
        session: checkoutSession
      }), /*#__PURE__*/_jsx(Total, {
        hasFutureCharges: hasFutureCharges,
        checkoutSession: checkoutSession,
        selectedOptionalLineItems: selectedItems
      })]
    }), maybeFutureCharges]
  });
};
export default OrderSummary;